import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is the Superbuy Spreadsheet?',
    answer: 'The Superbuy Spreadsheet is a meticulously curated digital catalog that organizes thousands of products across 14 categories — from sneakers and hoodies to accessories and fragrances. Each entry includes direct purchase links, price ranges, quality ratings, and when available, real QC photos so you can make informed buying decisions with confidence.',
  },
  {
    question: 'Is the Superbuy Spreadsheet free to use?',
    answer: 'Yes, the Superbuy Spreadsheet is completely free to access and browse. You can explore all categories, view product listings, check prices, and read quality ratings at no cost. We believe in making smarter shopping accessible to everyone.',
  },
  {
    question: 'How often is the spreadsheet updated?',
    answer: 'Our team updates the spreadsheet weekly to ensure all links remain active, prices reflect current market rates, and new product discoveries are added promptly. Outdated or broken links are removed quickly so you always have a reliable resource at your fingertips.',
  },
  {
    question: 'How do I know if a product listed is high quality?',
    answer: 'We include seller ratings, buyer reviews, and real QC (Quality Check) photos whenever available. These QC photos are actual photographs of the product taken before shipping, giving you an honest preview. We also mark verified sellers and flag listings that have consistent positive feedback from the community.',
  },
  {
    question: 'Is it safe to shop using the Superbuy Spreadsheet?',
    answer: 'Yes, when you follow the safety guidelines we provide. We include scam prevention tips, recommend secure payment methods with buyer protection, and maintain a community-driven alert system for unreliable sellers. Our detailed Safety Guide covers everything you need to know, from QC checks to shipping insurance and refund policies.',
  },
  {
    question: 'Can I request new categories or products?',
    answer: 'Absolutely! We welcome community suggestions for new product categories and specific items. You can submit requests through our main website. The team reviews all suggestions and prioritizes additions based on community demand and product availability.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="font-label text-xs md:text-sm uppercase tracking-[0.2em] text-accent-500 mb-3">
            Got Questions?
          </p>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-[1.15]">
            Frequently Asked
            <br />
            Questions
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background-100 rounded-xl border border-background-200/60 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-background-200/30 transition-colors duration-200"
              >
                <span className="font-heading text-base md:text-lg font-semibold text-foreground-900 pr-4">
                  {faq.question}
                </span>
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-background-50 flex items-center justify-center border border-background-300/60">
                  <i
                    className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-sm text-foreground-600 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-6 pb-5 font-body text-sm md:text-base text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Below FAQ */}
        <div className="text-center mt-10">
          <p className="font-body text-sm text-foreground-500 mb-4">
            Have more questions? Check out our detailed guides or visit the main store.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/article/ultimate-superbuy-spreadsheet-guide-2026"
              className="px-6 py-3 bg-background-100 text-foreground-800 font-label text-sm font-medium rounded-full border border-background-300/60 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Read Our Guide
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 bg-primary-500 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Visit Main Store
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}